import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';

const { FiEdit3, FiLayers, FiBookOpen, FiPlayCircle, FiCheckCircle, FiTarget } = FiIcons;

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 1, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Services() {
  const coreServices = [
    {
      title: "Writing and Messaging",
      description: "Clear, intentional communication that reflects your identity and resonates with your audience.",
      icon: FiEdit3
    },
    {
      title: "Strategic Development",
      description: "Vision clarity, offer structuring, and execution planning designed for real growth.",
      icon: FiLayers
    },
    {
      title: "Storytelling and Content",
      description: "Purpose-driven narratives that don’t just attract attention, but create connection.",
      icon: FiBookOpen
    },
    {
      title: "Video and Audio Production",
      description: "Professional, story-led media that elevates your presence and amplifies your message.",
      icon: FiPlayCircle
    }
  ];

  const outcomes = [
    "Lack of clarity in brand or direction",
    "Inconsistent or ineffective content",
    "Feeling stuck, overwhelmed, or unsure",
    "Building without alignment & frustration"
  ];

  return (
    <div className="w-full bg-kin-black">
      {/* ---------------------------------------------------------------------- */}
      {/* HERO SECTION */}
      {/* ---------------------------------------------------------------------- */}
      <section className="relative pt-48 pb-32 px-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_rgba(198,160,98,0.08)_0%,_transparent_50%)] pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <FadeUp>
            <p className="text-kin-gold tracking-[0.4em] text-xs uppercase font-light mb-8">
              Our Services
            </p>
          </FadeUp>
          
          <FadeUp delay={0.2}>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif text-kin-ivory mb-10 leading-[1.05]">
              Transforming Your <br />
              <span className="italic text-kin-gold">Vision Into Impact.</span>
            </h1>
          </FadeUp>

          <FadeUp delay={0.3}>
            <p className="max-w-3xl text-xl md:text-2xl text-kin-ivory/50 font-light leading-relaxed tracking-wide">
              At Kingdom Inspira Nova Studio, we don’t just offer services — we build aligned, high-impact creative systems around your vision.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ---------------------------------------------------------------------- */}
      {/* CORE SERVICES GRID */}
      {/* ---------------------------------------------------------------------- */}
      <section className="py-24 px-6 border-t border-kin-ivory/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-end mb-24 gap-8">
            <FadeUp className="max-w-2xl">
              <h2 className="text-3xl md:text-5xl font-serif text-kin-ivory mb-6">Our Core Services</h2>
              <p className="text-kin-ivory/40 text-lg font-light leading-relaxed">
                We provide a curated suite of services designed to bring clarity, structure, and compelling communication to your brand.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="h-px w-32 bg-kin-gold hidden lg:block mb-4"></div>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-kin-ivory/10 border border-kin-ivory/10">
            {coreServices.map((service, i) => (
              <div key={i} className="bg-kin-black p-12 md:p-16 group hover:bg-[#0E0E12] transition-all duration-700">
                <FadeUp delay={i * 0.1}>
                  <div className="mb-10 text-kin-gold group-hover:scale-110 transition-transform duration-700 origin-left">
                    <SafeIcon icon={service.icon} className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-serif text-kin-ivory mb-6 group-hover:text-kin-gold transition-colors duration-500">
                    {service.title}
                  </h3>
                  <p className="text-lg text-kin-ivory/50 font-light leading-relaxed group-hover:text-kin-ivory/70 transition-colors duration-500">
                    {service.description}
                  </p>
                </FadeUp>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------------- */}
      {/* WHO WE HELP */}
      {/* ---------------------------------------------------------------------- */}
      <section className="py-32 md:py-48 px-6 bg-[#09090b] overflow-hidden relative">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 text-[20vw] font-serif text-kin-gold/5 pointer-events-none select-none italic">
          Alignment
        </div>
        
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 items-center relative z-10">
          <div className="lg:w-1/2">
            <FadeUp>
              <h2 className="text-4xl md:text-6xl font-serif text-kin-ivory mb-12">Who We Help</h2>
              <div className="space-y-8 text-lg md:text-xl text-kin-ivory/60 font-light leading-loose tracking-wide">
                <p>
                  Our ideal clients are purpose-driven creators, entrepreneurs, and leaders who know they’re called to build something meaningful — but need clarity, structure, and direction to bring it to life.
                </p>
                <p className="text-kin-ivory italic border-l border-kin-gold pl-8 py-2">
                  They value clarity over hype, depth over surface-level tactics, and alignment over quick wins.
                </p>
                <p>
                  They are ready to invest, grow, and do the work; they just don’t want to keep guessing.
                </p>
              </div>
            </FadeUp>
          </div>
          <div className="lg:w-1/2">
            <FadeUp delay={0.2}>
              <div className="aspect-[4/5] bg-kin-black relative overflow-hidden border border-kin-ivory/5">
                <img 
                  src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=1200" 
                  alt="Creative strategy" 
                  className="w-full h-full object-cover opacity-40 grayscale"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-kin-black via-transparent to-transparent"></div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------------- */}
      {/* UNIQUE APPROACH */}
      {/* ---------------------------------------------------------------------- */}
      <section className="py-32 md:py-48 px-6 border-y border-kin-ivory/5">
        <div className="max-w-5xl mx-auto text-center">
          <FadeUp>
            <h2 className="text-sm font-sans tracking-[0.4em] uppercase text-kin-gold mb-12">Our Unique Approach</h2>
            <h3 className="text-4xl md:text-6xl font-serif text-kin-ivory mb-12 italic">Alignment First.</h3>
          </FadeUp>
          
          <FadeUp delay={0.2}>
            <div className="max-w-3xl mx-auto text-xl text-kin-ivory/60 font-light leading-relaxed space-y-10">
              <p>
                What makes Kingdom Inspira Nova Studio stand out is simple: we don’t start with marketing — we start with alignment.
              </p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-12 py-8">
                <span className="text-kin-gold font-serif italic text-2xl">Identity</span>
                <div className="h-px w-12 bg-kin-ivory/20 hidden md:block"></div>
                <span className="text-kin-gold font-serif italic text-2xl">Clarity</span>
                <div className="h-px w-12 bg-kin-ivory/20 hidden md:block"></div>
                <span className="text-kin-gold font-serif italic text-2xl">Message</span>
              </div>
              <p>
                Most approaches focus on tactics and trends. We help you understand who you are, what you’re building, and how to communicate it with precision.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------------------------------------------------------------------- */}
      {/* PROBLEMS WE SOLVE */}
      {/* ---------------------------------------------------------------------- */}
      <section className="py-32 md:py-48 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
            <FadeUp>
              <h2 className="text-3xl md:text-4xl font-serif text-kin-ivory mb-12">We Help Solve Problems Like:</h2>
              <div className="space-y-6">
                {outcomes.map((item, i) => (
                  <div key={i} className="flex items-center gap-6 group">
                    <div className="w-10 h-10 rounded-full border border-kin-gold/30 flex items-center justify-center text-kin-gold shrink-0 group-hover:bg-kin-gold group-hover:text-kin-black transition-all duration-500">
                      <SafeIcon icon={FiTarget} className="w-4 h-4" />
                    </div>
                    <p className="text-lg text-kin-ivory/70 font-light">{item}</p>
                  </div>
                ))}
              </div>
            </FadeUp>
            
            <FadeUp delay={0.3}>
              <div className="bg-[#0E0E12] p-12 md:p-16 border border-kin-gold/10 relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-kin-gold/5 blur-3xl"></div>
                <h2 className="text-3xl md:text-4xl font-serif text-kin-gold mb-8 italic">The Result?</h2>
                <ul className="space-y-6 text-xl text-kin-ivory font-serif italic">
                  <li className="flex items-center gap-4">
                    <SafeIcon icon={FiCheckCircle} className="text-kin-gold w-5 h-5 not-italic" />
                    Stronger messaging
                  </li>
                  <li className="flex items-center gap-4">
                    <SafeIcon icon={FiCheckCircle} className="text-kin-gold w-5 h-5 not-italic" />
                    More intentional content
                  </li>
                  <li className="flex items-center gap-4">
                    <SafeIcon icon={FiCheckCircle} className="text-kin-gold w-5 h-5 not-italic" />
                    Better connection with your audience
                  </li>
                </ul>
                <p className="mt-12 text-kin-ivory/50 font-light leading-relaxed">
                  A business or brand that finally feels aligned — not forced.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------------- */}
      {/* FINAL CTA */}
      {/* ---------------------------------------------------------------------- */}
      <section className="py-40 md:py-60 px-6 text-center border-t border-kin-ivory/5 relative overflow-hidden bg-kin-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(198,160,98,0.05)_0%,_transparent_70%)]"></div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <FadeUp>
            <h2 className="text-4xl md:text-6xl font-serif text-kin-ivory mb-16 italic">
              Ready to build with <span className="text-kin-gold">alignment?</span>
            </h2>
          </FadeUp>
          
          <FadeUp delay={0.2}>
            <Link to="/contact">
              <Button variant="primary" className="px-16 py-6 text-sm">
                Book A Discovery Call
              </Button>
            </Link>
          </FadeUp>
        </div>
      </section>

    </div>
  );
}